import { useState, useRef } from "react";
import { Documento, TipoDocumento } from "../../types";

interface SubirDocumentoModalProps {
  casoId?: number;
  analisisEstudioId?: number;
  usuarioId: number;
  onSubmit: (data: Omit<Documento, "id" | "fechaSubida">) => void;
  onCancel: () => void;
}

export function SubirDocumentoModal({
  casoId,
  analisisEstudioId,
  usuarioId,
  onSubmit,
  onCancel,
}: SubirDocumentoModalProps) {
  const [nombre, setNombre] = useState("");
  const [clasificacion, setClasificacion] = useState("");
  const [costoAsociado, setCostoAsociado] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clasificaciones = [
    "Poder",
    "Escrito de Fundamentos",
    "CIP",
    "Factibilidad Sanitaria",
    "Plano",
    "Escritura",
    "Calculo",
    "Comprobante SII",
    "Respuesta SII",
    "Certificado",
    "Otro",
  ];

  const getFileType = (file: File): TipoDocumento => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "pdf") return "pdf";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext || "")) return "imagen";
    if (["doc", "docx"].includes(ext || "")) return "word";
    if (["xls", "xlsx"].includes(ext || "")) return "excel";
    return "otro";
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    if (!nombre) {
      setNombre(file.name);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFile && nombre.trim()) {
      onSubmit({
        casoId,
        analisisEstudioId,
        nombre: nombre.trim(),
        tipo: getFileType(selectedFile),
        tamano: formatFileSize(selectedFile.size),
        clasificacion: clasificacion || undefined,
        costoAsociado: costoAsociado ? Number(costoAsociado) : undefined,
        subidoPor: usuarioId,
      });
    }
  };

  const isValid = selectedFile !== null && nombre.trim().length > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Dropzone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragging
            ? "border-blue-500 bg-blue-50"
            : selectedFile
            ? "border-green-500 bg-green-50"
            : "border-slate-300 hover:border-slate-400"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          className="hidden"
        />

        {selectedFile ? (
          <div>
            <svg
              className="w-12 h-12 mx-auto text-green-500 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm font-medium text-slate-800">
              {selectedFile.name}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {formatFileSize(selectedFile.size)}
            </p>
            <p className="text-xs text-blue-600 mt-2">
              Haz clic para cambiar el archivo
            </p>
          </div>
        ) : (
          <div>
            <svg
              className="w-12 h-12 mx-auto text-slate-400 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <p className="text-sm text-slate-600">
              Arrastra un archivo aqui o haz clic para seleccionar
            </p>
            <p className="text-xs text-slate-500 mt-1">
              PDF, Word, Excel, imagenes
            </p>
          </div>
        )}
      </div>

      {/* Nombre del archivo */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Nombre del Documento *
        </label>
        <input
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Nombre descriptivo del documento"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Clasificacion */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Clasificacion
        </label>
        <select
          value={clasificacion}
          onChange={(e) => setClasificacion(e.target.value)}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Sin clasificacion</option>
          {clasificaciones.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {/* Costo Asociado */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Costo Asociado ($)
        </label>
        <input
          type="number"
          value={costoAsociado}
          onChange={(e) => setCostoAsociado(e.target.value)}
          placeholder="Opcional"
          min="0"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Botones */}
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!isValid}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          Subir Documento
        </button>
      </div>
    </form>
  );
}
